import { MutationOptions } from "@apollo/client";
import apollo from "../../clients/apollo";
import {
  EndGamePlayInput,
  GameModeDataInput,
  GamePlayMode,
  Mutation,
  PurchaseInventoryInput,
  SignedUrlInput,
  UserMetadataInput,
  UserUpdateInput,
} from "../graphql";
import { followUserGQL, unfollowUserGQL } from "../mutations/friends";
import {
  applyInventoryGQL,
  purchaseInventoryGQL,
} from "../mutations/inventoriesMutationGql";
import { clearNotificationGQL } from "../mutations/notificationMutationGql";
import {
  claimFreeCoinsGQL,
  generateUploadURLGQL,
  updateUserGQL,
  updateUserMetadataGQL,
} from "../mutations/users";
import { endGamePlayGql, joinGamePlayGQL } from "../mutations";

const apolloClient = apollo.getInstance();

export const followUser = async (
  userId: string,
): Promise<Mutation["followUser"]> => {
  const res = await apolloClient.mutate({
    mutation: followUserGQL,
    variables: { userId },
  });
  return res.data.followUser;
};

export const unfollowUser = async (
  userId: string,
): Promise<Mutation["unfollowUser"]> => {
  const res = await apolloClient.mutate({
    mutation: unfollowUserGQL,
    variables: { userId },
  });
  return res.data.followUser;
};

export const purchaseInventory = async (
  input: PurchaseInventoryInput,
): Promise<Mutation["purchaseInventory"]> => {
  const res = await apolloClient.mutate({
    mutation: purchaseInventoryGQL,
    variables: { input },
  });

  return res.data.applyInventory;
};
export const applyInventory = async (
  inventoryId: string,
): Promise<Mutation["applyInventory"]> => {
  const res = await apolloClient.mutate({
    mutation: applyInventoryGQL,
    variables: { input: { inventoryId } },
  });

  return res.data.applyInventory;
};

export const clearNotifications = async (
  params: string[],
): Promise<Mutation["clearNotifications"]> => {
  const res = await apolloClient.mutate({
    mutation: clearNotificationGQL,
    variables: {
      input: params,
    },
  });

  return res.data.clearNotifications;
};

export const updateUser = async (
  input: Partial<UserUpdateInput>,
): Promise<Mutation["updateUser"]> => {
  const res = await apolloClient.mutate({
    variables: { input },
    mutation: updateUserGQL,
  });

  return res.data.updateUser;
};

export const updateUserMetadata = async (
  input: Partial<UserMetadataInput>,
  options: Partial<MutationOptions> = {},
): Promise<Mutation["updateUserMetadata"]> => {
  const res = await apolloClient.mutate({
    ...options,
    variables: { input },
    mutation: updateUserMetadataGQL,
  });

  return res.data;
};

export const claimFreeCoins = async (): Promise<Mutation["claimFreeCoins"]> => {
  const res = await apolloClient.mutate({
    mutation: claimFreeCoinsGQL,
  });

  return res.data.claimLoginReward;
};

export const generateUploadURL = async (
  input: Partial<SignedUrlInput>,
): Promise<Mutation["generateUploadURL"]> => {
  const res = await apolloClient.mutate({
    variables: { input },
    mutation: generateUploadURLGQL,
  });

  return res.data.generateUploadURL;
};

export const joinGamePlay = async (
  input: Partial<{
    gameId: string;
    gamePlayId: string;
    mode: GamePlayMode;
    modeData?:
      | { casualStreamConfig: { clusterUrl: string } }
      | GameModeDataInput;
    startImmediately?: boolean;
  }>,
): Promise<Mutation["joinGamePlay"]> => {
  const res = await apolloClient.mutate({
    variables: { input },
    mutation: joinGamePlayGQL,
  });

  return res.data.joinGamePlay;
};

export const endGamePlay = async (
  input: Partial<EndGamePlayInput>,
): Promise<Mutation["endGamePlay"]> => {
  const res = await apolloClient.mutate({
    variables: { input },
    mutation: endGamePlayGql,
  });

  return res.data.endGamePlay;
};
